/**
 * @file UserRowActions.tsx
 * @brief Per-row dropdown menu wired to all user mutations + the
 *        edit / change-password modals.
 *
 * @details
 * - Available actions adapt to the user's current `status`: a suspended
 *   user offers "Reactivate" instead of "Suspend"; a deleted user shows
 *   nothing destructive (already gone).
 * - Destructive actions (suspend / delete) prompt via `<AlertDialog>`.
 * - The current logged-in super admin cannot suspend or delete *themself*
 *   to avoid foot-guns.
 * - Owner rules (B31, ADR-059, 25-10-2026): the Owner's row has no edit,
 *   password, suspend, delete or 2FA reset; another Super Admin's password,
 *   suspend, delete and 2FA reset show to the Owner only. "Reset two-step
 *   verification" (2FA on only) asks first, signs the user out everywhere
 *   and emails them. The server enforces all of it (OWNER_PROTECTED /
 *   OWNER_ONLY); a refusal shows its message.
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  Ellipsis,
  FlaskConical,
  KeyRound,
  Mail,
  MailCheck,
  Pencil,
  RotateCcwClock,
  Shield,
  ShieldOff,
  ShieldX,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { useIsOwner } from "@/shared/auth/meExtras";
import { useResetUserTwoFactor } from "@/features/auth/twoFactor/twoFactorApi";
import { ownerRuleText } from "@/features/auth/twoFactor/twoFactorRules";
import { toast } from "@/shared/hooks/useToast";
import { deferAfterDropdownMenu } from "@/shared/utils/restoreBodyInteraction";
import { formatDateTime } from "@/shared/utils/formatters";
import {
  useDeleteUser,
  useSendUserDataExport,
  useReactivateUser,
  useResendWelcomeEmail,
  useSuspendUser,
  useUpdateUser,
} from "../api";
import type { AdminUser } from "../schemas";

interface UserRowActionsProps {
  user: AdminUser;
  onEdit: (user: AdminUser) => void;
  onChangePassword: (user: AdminUser) => void;
  onViewHistory: (user: AdminUser) => void;
}

type ConfirmKind = "suspend" | "delete" | "reset2fa" | null;

export function UserRowActions({
  user,
  onEdit,
  onChangePassword,
  onViewHistory,
}: UserRowActionsProps) {
  const { user: currentUser } = useAuth();
  const isSelf = currentUser?.id === user.id;
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const viewerIsOwner = useIsOwner();
  /** The Owner's row: nothing that changes the account (B31). */
  const ownerRow = user.is_owner === true;
  /** Another Super Admin, seen by anyone but the Owner. */
  const staffLocked = user.role === "superadmin" && !viewerIsOwner;
  const protectedRow = ownerRow || staffLocked;
  const resetTwoFactor = useResetUserTwoFactor();

  const suspend = useSuspendUser();
  const reactivate = useReactivateUser();
  const remove = useDeleteUser();
  const resend = useResendWelcomeEmail();
  const sendExport = useSendUserDataExport();
  const update = useUpdateUser();
  // Internal (our own) account flag, superadmin only (18-10-2026). Hidden
  // until the server sends it, so an older backend never gets a write it drops.
  const internalKnown = typeof user.is_internal === "boolean";
  const handleToggleInternal = () =>
    runMutation(
      () => update.mutateAsync({ id: user.id, input: { is_internal: !user.is_internal } }),
      user.is_internal
        ? `${user.email} counts as a customer again`
        : `${user.email} marked internal: left out of totals`,
    );

  // ----------------------------------------------------------------------
  // Action handlers — all wrap mutations in toast feedback.
  // ----------------------------------------------------------------------
  const runMutation = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      await action();
      toast({ variant: "success", title: successMessage });
    } catch (err) {
      const ownerRule = err instanceof ApiClientError ? ownerRuleText(err.code, err.message) : null;
      toast({
        variant: "destructive",
        title: ownerRule ? "Not allowed" : "Action failed",
        description:
          ownerRule ??
          (err instanceof ApiClientError
            ? err.message
            : "Please try again."),
      });
    }
  };

  const handleResetTwoFactor = () =>
    runMutation(
      () => resetTwoFactor.mutateAsync(user.id),
      `Two-step verification reset for ${user.email}`,
    );

  // The zip goes to the account's own email as a 7-day link; staff never
  // get it (B2/W4, DES 22-10-2026).
  const handleSendExport = async () => {
    try {
      const res = await sendExport.mutateAsync(user.id);
      toast({
        variant: "success",
        title: `Sent to ${res.sentTo ?? user.email}`,
        description: res.expiresAt
          ? `The link works until ${formatDateTime(res.expiresAt)}.`
          : undefined,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title:
          err instanceof ApiClientError && err.code === "EXPORT_EMAIL_FAILED"
            ? "The export email did not go"
            : "Action failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  const handleSuspend = () =>
    runMutation(
      () => suspend.mutateAsync(user.id),
      `${user.email} suspended`,
    );

  const handleReactivate = () =>
    runMutation(
      () => reactivate.mutateAsync(user.id),
      `${user.email} reactivated`,
    );

  const handleDelete = () =>
    runMutation(() => remove.mutateAsync(user.id), `${user.email} deleted`);

  const handleResendWelcome = () =>
    runMutation(
      () => resend.mutateAsync(user.id),
      `Welcome email re-sent to ${user.email}`,
    );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Actions for ${user.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Ellipsis className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {!ownerRow ? (
            <DropdownMenuItem
              onSelect={() => deferAfterDropdownMenu(() => onEdit(user))}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </DropdownMenuItem>
          ) : null}

          {!protectedRow ? (
            <DropdownMenuItem
              onSelect={() =>
                deferAfterDropdownMenu(() => onChangePassword(user))
              }
            >
              <KeyRound className="h-4 w-4" />
              Change password
            </DropdownMenuItem>
          ) : null}

          {/* Lost phone and backup codes (B31): support turns 2FA off. */}
          {!protectedRow && user.two_factor_enabled === true ? (
            <DropdownMenuItem
              onSelect={() => deferAfterDropdownMenu(() => setConfirm("reset2fa"))}
              disabled={isSelf}
            >
              <ShieldX className="h-4 w-4" />
              Reset two-step verification
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuItem
            onSelect={() => deferAfterDropdownMenu(() => onViewHistory(user))}
          >
            <RotateCcwClock className="h-4 w-4" />
            Login history
          </DropdownMenuItem>

          {internalKnown && currentUser?.role === "superadmin" ? (
            <DropdownMenuItem disabled={update.isPending} onSelect={handleToggleInternal}>
              <FlaskConical className="h-4 w-4" />
              {user.is_internal ? "Mark as customer account" : "Mark as internal account"}
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuItem onSelect={handleResendWelcome}>
            <Mail className="h-4 w-4" />
            Resend welcome email
          </DropdownMenuItem>

          {/* GDPR request sent to Verblizr: confirm identity first. Works for
              suspended and deleted (not yet purged) accounts too. The data
              goes to the account email, never to staff (B2/W4). */}
          <DropdownMenuItem
            disabled={sendExport.isPending}
            onSelect={() => void handleSendExport()}
          >
            <MailCheck className="h-4 w-4" />
            {sendExport.isPending ? "Sending export…" : "Send export to the account email"}
          </DropdownMenuItem>

          {!protectedRow ? <DropdownMenuSeparator /> : null}

          {protectedRow ? null : user.status === "active" ? (
            <DropdownMenuItem
              onSelect={() =>
                deferAfterDropdownMenu(() => setConfirm("suspend"))
              }
              disabled={isSelf}
              destructive
            >
              <ShieldOff className="h-4 w-4" />
              Suspend
            </DropdownMenuItem>
          ) : user.status === "suspended" ? (
            <DropdownMenuItem onSelect={handleReactivate}>
              <Shield className="h-4 w-4" />
              Reactivate
            </DropdownMenuItem>
          ) : null}

          {!protectedRow && user.status !== "deleted" ? (
            <DropdownMenuItem
              onSelect={() =>
                deferAfterDropdownMenu(() => setConfirm("delete"))
              }
              disabled={isSelf}
              destructive
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ----------------------------------------------------------------------
          Suspend confirmation
          ---------------------------------------------------------------------- */}
      <AlertDialog
        open={confirm === "suspend"}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend this user?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{user.email}</span>{" "}
              will not be able to sign in until reactivated. Active sessions
              continue to bill normally.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-warning text-warning-foreground hover:bg-warning/90"
              onClick={async () => {
                setConfirm(null);
                await handleSuspend();
              }}
            >
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ----------------------------------------------------------------------
          Reset two-step verification confirmation (B31)
          ---------------------------------------------------------------------- */}
      <AlertDialog
        open={confirm === "reset2fa"}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset two-step verification?</AlertDialogTitle>
            <AlertDialogDescription>
              Two-step verification is turned off for{" "}
              <span className="font-medium text-foreground">{user.email}</span>.
              They are signed out on every device and emailed. Only do this
              once you are sure who is asking: anyone with their password can
              then sign in. If their account requires it, they set it up again
              at their next sign-in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                setConfirm(null);
                await handleResetTwoFactor();
              }}
            >
              Reset two-step verification
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ----------------------------------------------------------------------
          Delete confirmation
          ---------------------------------------------------------------------- */}
      <AlertDialog
        open={confirm === "delete"}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This is a soft delete: the user is marked as deleted and can no
              longer sign in. Their historical sessions and invoices are
              preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                setConfirm(null);
                await handleDelete();
              }}
            >
              Delete user
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
